import * as Yup from 'yup';
import { ShieldCheck, ShieldOff } from 'lucide-react';

export const NOT_LISTED = 'not_listed';

export const filterContacts = (contacts = [], query = '') => {
  const term = query.trim().toLowerCase();
  if (!term) return contacts;

  const digits = term.replace(/\D/g, '');

  return contacts.filter(({ attributes }) => {
    const { first_name = '', last_name = '', relationship = '', phone = '', mobile_phone = '' } = attributes || {};
    const text = `${first_name} ${last_name} ${relationship}`.toLowerCase();
    const number = (phone || mobile_phone || '').replace(/\D/g, '');

    return text.includes(term) || (digits && number.includes(digits));
  });
};

export const buildPayload =({ person_id, person_type, first_name, last_name, relationship, phone }) => ({
  authorized_contact: person_id === NOT_LISTED
    ? { first_name, last_name, relationship, phone: phone.replace(/\D/g, '') }
    : { person_id, person_type },
});

export const SOURCE_COLORS = {
  'Family member': 'bg-blue-50 text-teamcare-blue',
  'Emergency contact': 'bg-amber-50 text-amber-700',
  'Added manually': 'bg-gray-100 text-secondary-500',
};

export const CONFIRM = {
  revoke: {
    message: (name) => `Revoke access for ${name}? Staff will stop discussing your information with them right away.`,
    cancel: 'Keep access',
    confirm: 'Revoke access',
    wrapper: 'border-red-100 bg-red-50/60',
    button: 'bg-error hover:bg-red-600',
  },
  reauthorize: {
    message: (name) => `Re-authorize ${name}? Staff will be able to discuss your appointments, billing and treatment with them.`,
    cancel: 'Cancel',
    confirm: 'Authorize',
    wrapper: 'border-success-100 bg-success-50/60',
    button: 'bg-success-600 hover:bg-success-700',
  },
};

export const TABS = {
  AUTHORIZED: 'authorized',
  REVOKED: 'revoked',
};

export const TAB_OPTIONS = [
  { id: TABS.AUTHORIZED, label: 'Authorized', activeBorder: '!border-success-500', pill: 'bg-success-50 text-success-700' },
  { id: TABS.REVOKED, label: 'Revoked', activeBorder: '!border-secondary-400', pill: 'bg-gray-100 text-secondary-500' },
];

export const EMPTY_STATES = {
  [TABS.AUTHORIZED]: {
    icon: ShieldCheck,
    iconClassName: 'bg-success-100 text-success-600',
    title: 'No one authorized yet',
    description: 'Our staff can only discuss your care with people you authorize.',
  },
  [TABS.REVOKED]: {
    icon: ShieldOff,
    iconClassName: 'bg-gray-100 text-secondary-400',
    title: 'No revoked contacts',
    description: 'People whose access you remove will show up here.',
  },
};

export const RELATIONSHIPS =['Spouse / partner', 'Parent', 'Child', 'Sibling', 'Guardian', 'Caregiver', 'Friend', 'Other'];

export const INITIAL_VALUES = {
  person_id: '',
  person_type: '',
  first_name: '',
  last_name: '',
  phone: '',
  relationship: '',
  agreed: false,
};

const whenNotListed =(schema: Yup.StringSchema) =>
  Yup.string().when('person_id', { is: NOT_LISTED, then: () => schema, otherwise: (s) => s.notRequired() });

export const validationSchema = Yup.object().shape({
  person_id: Yup.string().required('Choose who you’d like to authorize'),
  first_name: whenNotListed(Yup.string().trim().max(20, 'Must be 20 characters or less').required('Required')),
  last_name: whenNotListed(Yup.string().trim().max(20, 'Must be 20 characters or less').required('Required')),
  phone: whenNotListed(
    Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Must be in format (000) 000-0000').required('Required'),
  ),
  relationship: whenNotListed(Yup.string().required('Required')),
  agreed: Yup.boolean().oneOf([true], 'Tick the box to confirm the authorization'),
});
